import React, { useState, useRef, useEffect } from "react";
import { ChatSession, ChatMessage } from "../services/decisionSupport";
import { getAnalysisTypeIcon } from "./DecisionSupportSidebar";
import KnowledgeBasePanel from "./KnowledgeBasePanel";
import "./DecisionSupportPanel.css";

interface DecisionSupportPanelProps {
  activeSession: ChatSession | null;
  messages: ChatMessage[];
  onSendMessage: (content: string) => Promise<void>;
  isSending: boolean;
  isLoadingMessages: boolean;
  streamingContent?: string;
  isStreaming?: boolean;
}

interface RAGChunk {
  content: string;
  source: string;
  page_number?: number;
  score: number;
  chunk_id: string;
}

const DecisionSupportPanel: React.FC<DecisionSupportPanelProps> = ({
  activeSession,
  messages,
  onSendMessage,
  isSending,
  isLoadingMessages,
  streamingContent = "",
  isStreaming = false,
}) => {
  const [inputMessage, setInputMessage] = useState("");
  const [kbPanelOpen, setKbPanelOpen] = useState(false);
  const [selectedChunks, setSelectedChunks] = useState<RAGChunk[]>([]);
  const [collapseAllChunks, setCollapseAllChunks] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom when new messages arrive or streaming content updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, streamingContent]);

  // Close knowledge base panel when session changes
  useEffect(() => {
    setKbPanelOpen(false);
  }, [activeSession?.id]);

  // Handle clicks on source references
  useEffect(() => {
    const handleSourceRefClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains("source-reference")) {
        const sourceNum = parseInt(target.getAttribute("data-source") || "0");
        if (sourceNum > 0) {
          // Find the message containing this source reference
          const messageElement = target.closest(".message--assistant");
          if (messageElement) {
            const messageId = messageElement.getAttribute("data-message-id");
            const message = messages.find((m) => m.id.toString() === messageId);
            if (message && message.extra_data?.rag_chunks) {
              handleSourceClick(sourceNum, message.extra_data.rag_chunks);
            }
          }
        }
      }
    };

    document.addEventListener("click", handleSourceRefClick);
    return () => document.removeEventListener("click", handleSourceRefClick);
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputMessage]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inputMessage.trim() || isSending) return;

    const message = inputMessage.trim();
    setInputMessage("");

    try {
      await onSendMessage(message);
    } catch (error) {
      console.error("Failed to send message:", error);
      // Put the message back if it failed
      setInputMessage(message);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleViewSources = (chunks: RAGChunk[]) => {
    setSelectedChunks(chunks);
    setKbPanelOpen(true);
    // Trigger collapse all when opening via button
    setCollapseAllChunks(true);
    setTimeout(() => setCollapseAllChunks(false), 50);
  };

  const handleSourceClick = (sourceNumber: number, chunks: RAGChunk[]) => {
    setSelectedChunks(chunks);
    setKbPanelOpen(true);
    // Pass the source number to the panel for navigation
    // We'll need to modify the panel to accept this
    setTimeout(() => {
      // Trigger scroll and expand after panel opens
      const event = new CustomEvent("navigateToChunk", {
        detail: { chunkIndex: sourceNumber - 1 },
      });
      window.dispatchEvent(event);
    }, 300); // Wait for panel animation
  };

  const formatTimestamp = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  // Format message content with markdown-like formatting and clickable source references
  const formatMessageContent = (content: string, ragChunks?: RAGChunk[]) => {
    const lines = content.split("\n");
    const formattedElements: React.ReactElement[] = [];
    let currentList: string[] = [];
    let currentListType: "ul" | "ol" | null = null;
    let inCodeBlock = false;
    let codeBlockLines: string[] = [];

    const flushList = () => {
      if (currentList.length > 0 && currentListType) {
        const ListTag = currentListType;
        formattedElements.push(
          <ListTag key={`list-${formattedElements.length}`}>
            {currentList.map((item, idx) => (
              <li
                key={idx}
                dangerouslySetInnerHTML={{ __html: formatInlineStyles(item) }}
              />
            ))}
          </ListTag>
        );
        currentList = [];
        currentListType = null;
      }
    };

    const flushCodeBlock = () => {
      if (codeBlockLines.length > 0) {
        formattedElements.push(
          <pre key={`code-${formattedElements.length}`}>
            <code>{codeBlockLines.join("\n")}</code>
          </pre>
        );
        codeBlockLines = [];
      }
    };

    const formatInlineStyles = (text: string): string => {
      // Bold: **text** or __text__
      text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
      text = text.replace(/__(.*?)__/g, "<strong>$1</strong>");
      // Italic: *text* or _text_
      text = text.replace(/\*(.*?)\*/g, "<em>$1</em>");
      text = text.replace(/_(.*?)_/g, "<em>$1</em>");
      // Inline code: `code`
      text = text.replace(/`([^`]+)`/g, "<code>$1</code>");
      // Source references: [Source N] or [Source N, M]
      if (ragChunks && ragChunks.length > 0) {
        text = text.replace(
          /\[Source\s+(\d+(?:\s*,\s*\d+)*)\]/g,
          (_match: string, numbers: string) => {
            const sourceNumbers = numbers
              .split(",")
              .map((n: string) => parseInt(n.trim()));
            const refs = sourceNumbers
              .map(
                (num: number) =>
                  `<span class="source-reference" data-source="${num}" title="View Source ${num}">[${num}]</span>`
              )
              .join(", ");
            return refs;
          }
        );
      }
      return text;
    };

    lines.forEach((line, index) => {
      // Check for code block markers
      if (line.trim() === "```" || line.trim().startsWith("```")) {
        if (inCodeBlock) {
          flushCodeBlock();
          inCodeBlock = false;
        } else {
          flushList();
          inCodeBlock = true;
        }
        return;
      }

      if (inCodeBlock) {
        codeBlockLines.push(line);
        return;
      }

      // Headers
      if (line.match(/^#{1,6}\s/)) {
        flushList();
        const level = line.match(/^#+/)?.[0].length || 1;
        const text = line.replace(/^#+\s/, "");
        const headerLevel = Math.min(level, 6) as 1 | 2 | 3 | 4 | 5 | 6;

        switch (headerLevel) {
          case 1:
            formattedElements.push(
              <h1
                key={index}
                dangerouslySetInnerHTML={{ __html: formatInlineStyles(text) }}
              />
            );
            break;
          case 2:
            formattedElements.push(
              <h2
                key={index}
                dangerouslySetInnerHTML={{ __html: formatInlineStyles(text) }}
              />
            );
            break;
          case 3:
            formattedElements.push(
              <h3
                key={index}
                dangerouslySetInnerHTML={{ __html: formatInlineStyles(text) }}
              />
            );
            break;
          case 4:
            formattedElements.push(
              <h4
                key={index}
                dangerouslySetInnerHTML={{ __html: formatInlineStyles(text) }}
              />
            );
            break;
          case 5:
            formattedElements.push(
              <h5
                key={index}
                dangerouslySetInnerHTML={{ __html: formatInlineStyles(text) }}
              />
            );
            break;
          case 6:
            formattedElements.push(
              <h6
                key={index}
                dangerouslySetInnerHTML={{ __html: formatInlineStyles(text) }}
              />
            );
            break;
        }
        return;
      }

      // Unordered list
      const ulMatch = line.match(/^[\s]*[-*+]\s+(.+)/);
      if (ulMatch) {
        if (currentListType !== "ul") {
          flushList();
          currentListType = "ul";
        }
        currentList.push(ulMatch[1]);
        return;
      }

      // Ordered list
      const olMatch = line.match(/^[\s]*\d+\.\s+(.+)/);
      if (olMatch) {
        if (currentListType !== "ol") {
          flushList();
          currentListType = "ol";
        }
        currentList.push(olMatch[1]);
        return;
      }

      // Regular line
      flushList();
      if (line.trim() === "") {
        formattedElements.push(<br key={index} />);
      } else {
        formattedElements.push(
          <p
            key={index}
            dangerouslySetInnerHTML={{ __html: formatInlineStyles(line) }}
          />
        );
      }
    });

    flushList();
    flushCodeBlock();

    return formattedElements;
  };

  // Show empty state when no session is selected
  if (!activeSession) {
    return (
      <div className="decision-support-panel">
        <div className="decision-support-panel__empty">
          <span className="empty-state__icon">💬</span>
          <h3>No Session Selected</h3>
          <p>
            Select a session from the sidebar or create a new one to start
            analyzing documents.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="decision-support-panel-wrapper">
      <div
        className={`decision-support-panel ${
          kbPanelOpen ? "decision-support-panel--kb-open" : ""
        }`}
      >
      {/* Session Header */}
      <div className="session-header">
        <div className="session-header__main">
          <span className="session-header__icon">
            {getAnalysisTypeIcon(activeSession.analysis_type)}
          </span>
          <h2 className="session-header__title">{activeSession.title}</h2>

          {/* Files Metadata */}
          {activeSession.files_metadata &&
            activeSession.files_metadata.length > 0 && (
              <div className="session-header__files">
                <span className="files-badge">
                  📎 {activeSession.files_metadata.length} file
                  {activeSession.files_metadata.length > 1 ? "s" : ""}
                </span>
                <div className="files-tooltip">
                  {activeSession.files_metadata.map((file, index) => (
                    <div key={index} className="file-item">
                      <span className="file-item__icon">📄</span>
                      <span className="file-item__name">{file.filename}</span>
                      <span className="file-item__size">
                        ({(file.size / 1024 / 1024).toFixed(2)} MB)
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Messages Area */}
      <div className="messages-container">
        {isLoadingMessages ? (
          <div className="messages-loading">
            <div className="spinner"></div>
            <p>Loading conversation...</p>
          </div>
        ) : (
          <>
            {messages.map((message) => (
              <div
                key={message.id}
                className={`message message--${message.role}`}
                  data-message-id={message.id}
              >
                <div className="message__content">
                  <div className="message__header">
                    <span className="message__role">
                      {message.role === "user" ? "You" : "AI Assistant"}
                    </span>
                    <span className="message__time">
                      {formatTimestamp(message.created_at)}
                    </span>
                  </div>
                  <div className="message__text">
                      {formatMessageContent(
                        message.content,
                        message.extra_data?.rag_chunks
                      )}
                  </div>
                  {message.role === "assistant" && (
                    <div className="message__actions">
                      <button
                        className="message__action-btn"
                        onClick={() => copyToClipboard(message.content)}
                        title="Copy to clipboard"
                      >
                        📋 Copy
                      </button>
                        {message.extra_data?.rag_chunks &&
                          message.extra_data.rag_chunks.length > 0 && (
                            <button
                              className="message__action-btn message__view-sources-btn"
                              onClick={() =>
                                handleViewSources(
                                  message.extra_data.rag_chunks!
                                )
                              }
                              title="View retrieved sources"
                            >
                              📚 Sources ({message.extra_data.rag_chunks.length}
                              )
                            </button>
                          )}
                      {message.extra_data?.total_tokens && (
                        <span className="message__tokens">
                          {message.extra_data.total_tokens.toLocaleString()}{" "}
                          tokens
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {isStreaming && streamingContent && (
              <div className="message message--assistant">
                <div className="message__content">
                  <div className="message__header">
                    <span className="message__role">AI Assistant</span>
                    <span className="message__status">● Streaming...</span>
                  </div>
                  <div className="message__text">
                    {formatMessageContent(streamingContent)}
                  </div>
                </div>
              </div>
            )}
            {isStreaming && !streamingContent && (
              <div className="message message--assistant">
                <div className="message__content">
                  <div className="message__header">
                    <span className="message__role">AI Assistant</span>
                  </div>
                  <div className="message__typing">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Input Area */}
      <div className="input-area">
        <form onSubmit={handleSubmit} className="input-form">
          <textarea
            ref={textareaRef}
            className="input-textarea"
            placeholder="Ask a follow-up question about the analysis..."
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isSending || isLoadingMessages}
            rows={1}
          />
          <button
            type="submit"
            className="send-button"
            disabled={!inputMessage.trim() || isSending || isLoadingMessages}
          >
            {isSending ? "⏳" : "➤"}
          </button>
        </form>
      </div>
      </div>

      {/* Knowledge Base Panel */}
      <KnowledgeBasePanel
        chunks={selectedChunks}
        isOpen={kbPanelOpen}
        onClose={() => setKbPanelOpen(false)}
        collapseAll={collapseAllChunks}
      />
    </div>
  );
};

export default DecisionSupportPanel;
